import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { cn } from '@rocco/utils/cn';

type Theme = 'light' | 'dark';

export interface DetailBylineRowProps {
    /** Where Back goes when this page opened the tab and there is nothing behind it. */
    backFallbackLink: string;
    /** The word before the name: "by" for the maker, "featuring" for the article's subject. */
    byLabel?: string;
    /** Who the thing is by. Linked to their profile when `link` is set. */
    professional?: { title: string; link?: string };
    /** Ranged right to the far gutter: the place, or the date. */
    trailing?: ReactNode;
    /** `light` = ink on the pale page; `dark` = white on a photograph. */
    theme?: Theme;
    className?: string;
}

/**
 * The row under a detail page's title (Figma 134:12939): Back at the gutter,
 * the byline in a 51rem track from it, and the place or date ranged right to
 * the far gutter. One row so the product, project and article open alike.
 *
 * Back goes through the router rather than `history.back()`, so it is a real
 * POP — the browse grid's scroll restore listens for that, and the reader
 * returns to the row they left rather than the top. Index 0 means this page
 * opened the tab, so there is nothing behind it to step to.
 */
export const DetailBylineRow = ({
    backFallbackLink,
    byLabel = 'by',
    professional,
    trailing,
    theme = 'light',
    className,
}: DetailBylineRowProps) => {
    const navigate = useNavigate();
    const dark = theme === 'dark';

    const handleBack = () => {
        const historyIndex =
            typeof window === 'undefined'
                ? 0
                : ((window.history.state as { idx?: number } | null)?.idx ?? 0);
        if (historyIndex > 0) {
            navigate(-1);
            return;
        }
        navigate(backFallbackLink);
    };

    const muted = dark ? 'text-white/60' : 'text-[#b1b1b1]';

    return (
        <div
            className={cn(
                'font-sans font-medium max-md:mt-6 max-md:flex max-md:flex-col max-md:gap-1 max-md:text-base md:mt-[3rem] md:grid md:grid-cols-[51rem_1fr] md:items-baseline md:text-[1.5rem]',
                className,
            )}
        >
            <button
                type="button"
                onClick={handleBack}
                className={cn(
                    // Desktop only: on the phone the browser's own back (and
                    // the app chrome's disc, where a page mounts one) covers
                    // the journey, and the text control led the masthead's
                    // byline with a step nobody needed.
                    'max-md:hidden',
                    'm-0 inline-flex cursor-pointer items-center gap-[0.5em] justify-self-start border-0 bg-transparent p-0 text-left leading-[1.2] transition-colors',
                    dark
                        ? 'text-white/60 hover:text-white'
                        : 'text-[#b1b1b1] hover:text-[#323232]',
                )}
            >
                {/* Drawn rather than typed: the triangle glyphs sit on
                    different baselines from font to font, and this one has to
                    align with a 24px line of text. Sized in `em` so it tracks
                    the label at every breakpoint. */}
                <svg
                    aria-hidden="true"
                    viewBox="0 0 6 10"
                    className="h-[0.5em] w-[0.3em] shrink-0"
                    fill="currentColor"
                >
                    <polygon points="6,0 6,10 0,5" />
                </svg>
                Back
            </button>

            <div className="md:flex md:items-baseline md:justify-between">
                {professional ? (
                    // A real space between the label and the name, not a flex
                    // gap: a gap is drawn, not written, so the line would copy
                    // and read aloud as "byCOCOHILLS".
                    <p className="m-0 leading-[1.2]">
                        <span className={cn('mr-[0.25rem]', muted)}>
                            {byLabel}
                        </span>{' '}
                        {professional.link ? (
                            // Opens in its own tab: the reader is mid-page,
                            // and the profile is a detour rather than the
                            // next step.
                            <a
                                href={professional.link}
                                target="_blank"
                                rel="noopener"
                                className={cn(
                                    'no-underline transition-colors',
                                    dark
                                        ? 'text-white hover:text-white/70'
                                        : 'text-[#323232] hover:text-[#767676]',
                                )}
                            >
                                {professional.title}
                            </a>
                        ) : (
                            <span
                                className={
                                    dark ? 'text-white' : 'text-[#323232]'
                                }
                            >
                                {professional.title}
                            </span>
                        )}
                    </p>
                ) : (
                    <span />
                )}
                {trailing ? (
                    <p className={cn('m-0 leading-[1.2]', muted)}>{trailing}</p>
                ) : null}
            </div>
        </div>
    );
};
